import React, { useState } from "react";
import { Col, Row, Spinner, Form, Dropdown } from "react-bootstrap";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import CardDropdown from "components/common/CardDropdown";
import IconButton from "components/common/IconButton";
import SubtleBadge from "components/common/SubtleBadge";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useSecurityQuestions from "hooks/accountManagement/useSecurityQuestions";

import AddSecurityQuestionModal from "./addModal";
import ViewSecurityQuestionModal from "./viewModal";
import EditSecurityQuestionModal from "./editModal";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";

import TablePaginationFooter from "components/common/TablePaginationFooter";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";

const SecurityQnsTable = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const {
    securityQns,
    loading,
    error,
    fetchSecurityQuestions,
    addSecurityQuestion,
    updateSecurityQuestion,
    deleteSecurityQuestion,
    totalPages,
    totalElements,
  } = useSecurityQuestions(page, pageSize);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionToDelete, setQuestionToDelete] = useState(null);

  const formatID = (id) => (id ? `${id.slice(0, 8)}...` : "");
  const formatText = (text) => (text ? `${text.slice(0, 40)}...` : "");

  const columns = [
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ getValue }) => formatID(getValue()),
      meta: { headerProps: { className: "text-900" } },
    },
    {
      accessorKey: "questionText",
      header: "Question Text",
      cell: ({ getValue }) => formatText(getValue()),
      meta: { headerProps: { className: "text-900" } },
    },
    {
      accessorKey: "active",
      header: "Status",
      cell: ({ getValue }) => (
        <SubtleBadge pill bg={getValue() ? "success" : "danger"}>
          {getValue() ? "Active" : "Inactive"}
        </SubtleBadge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const question = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Dropdown.Item
                onClick={() => {
                  setSelectedQuestion(question);
                  setShowViewModal(true);
                }}
              >
                View
              </Dropdown.Item>
              <Guard privilege={PRIV.UPDATE_QUESTIONS}>
                <Dropdown.Item
                  onClick={() => {
                    setSelectedQuestion(question);
                    setShowUpdateModal(true);
                  }}
                >
                  Edit
                </Dropdown.Item>
              </Guard>
              <Guard privilege={PRIV.DELETE_QUESTIONS}>
                <Dropdown.Divider />
                <Dropdown.Item
                  className="text-danger"
                  onClick={() => {
                    setQuestionToDelete(question.id);
                    setSelectedQuestion(question);
                    setShowDeleteModal(true);
                  }}
                >
                  Delete
                </Dropdown.Item>
              </Guard>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-center text-900" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: securityQns,
    columns,
    sortable: true,
    pagination: true,
    perPage: 50, // Match RoutesTable pattern
  });

  const handleDelete = async () => {
    if (!questionToDelete) return;
    await deleteSecurityQuestion(questionToDelete);
    setShowDeleteModal(false);
    setQuestionToDelete(null);
    setSelectedQuestion(null);
  };

  if (loading && securityQns.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" variant="success" />
        <span className="ms-3">Loading security questions...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger d-flex align-items-center justify-content-between">
        <div>Error loading security questions: {error}</div>
      </div>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="question-circle"
        title="Security Questions"
        subtitle="Manage security questions for user accounts"
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <AdvanceTableProvider {...table}>
        {" "}
        <Row className="mb-3 g-3">
          <Col md={6} lg={5}>
            <AdvanceTableSearchBox placeholder="Search questions..." />
          </Col>
          <Col md={6} lg={7} className="text-end">
            <Guard privilege={PRIV.CREATE_QUESTIONS}>
              <IconButton
                variant="falcon-default"
                size="sm"
                icon="plus"
                transform="shrink-3"
                onClick={() => setShowAddModal(true)}
              >
                <span className="d-none d-sm-inline-block ms-1">
                  New Security Question
                </span>
              </IconButton>
            </Guard>
          </Col>
        </Row>
        <AdvanceTable
          headerClassName="bg-200 text-nowrap align-middle"
          rowClassName="btn-reveal-trigger text-nowrap align-middle"
          tableProps={{
            bordered: true,
            striped: true,
            className: "fs-10 mb-0 overflow-hidden",
          }}
        />
        <div className="mt-3">
          <TablePaginationFooter
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            onPrev={() => setPage((prev) => Math.max(prev - 1, 0))}
            onNext={() => setPage((prev) => Math.min(prev + 1, totalPages - 1))}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            pageSizeOptions={[10, 20, 30, 50, 100, 200, 500]}
          />
        </div>
      </AdvanceTableProvider>

      {/* Modals */}
      <AddSecurityQuestionModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        addSecurityQuestion={addSecurityQuestion}
        fetchSecurityQuestions={fetchSecurityQuestions}
      />
      <ViewSecurityQuestionModal
        show={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedQuestion(null);
        }}
        question={selectedQuestion}
        loading={loading}
      />
      <EditSecurityQuestionModal
        show={showUpdateModal}
        onClose={() => {
          setShowUpdateModal(false);
          setSelectedQuestion(null);
        }}
        selectedQuestion={selectedQuestion}
        updateSecurityQuestion={updateSecurityQuestion}
        fetchSecurityQuestions={fetchSecurityQuestions}
      />
      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        itemName={selectedQuestion?.questionText || "this security question"}
        onConfirm={handleDelete}
        loading={loading}
      />
    </>
  );
};

export default SecurityQnsTable;